// Punto de inyección para avisarle algo al usuario desde el SDK.
//
// El SDK es la capa de datos y lo consumen tanto las apps web como la
// super-app (Expo). Importar `sonner` acá acoplaba TODA app consumidora a un
// componente web (YGO-261): en React Native ni siquiera existe el DOM donde
// montarlo.
//
// La solución no es sacar los avisos —el SDK sí tiene cosas que decir, como el
// error de una mutation que nadie manejó (YGO-279)— sino invertir la
// dependencia: el SDK declara QUÉ avisar, cada app decide CÓMO mostrarlo.
// Web registra sonner, la super-app registra `Alert.alert`.

export type NotificationLevel = 'info' | 'success' | 'warning' | 'error';

export type NotificationOptions = {
  /** Texto secundario, cuando el destino lo soporta. */
  description?: string;
};

export type Notifier = (
  level: NotificationLevel,
  title: string,
  options?: NotificationOptions
) => void;

/**
 * Fallback deliberado: si una app se olvida de registrar su notificador, el
 * aviso va a la consola en vez de desaparecer. Tragarlo en silencio sería
 * reintroducir justo el problema que YGO-279 quiere hacer imposible.
 */
const consoleNotifier: Notifier = (level, title, options) => {
  const message = options?.description
    ? `${title} — ${options.description}`
    : title;
  if (level === 'error') console.error(`[ygoarg] ${message}`);
  else console.warn(`[ygoarg] ${message}`);
};

let currentNotifier: Notifier = consoleNotifier;

/** La registra cada app al arrancar. */
export const setNotifier = (notifier: Notifier): void => {
  currentNotifier = notifier;
};

/** Vuelve al fallback de consola. Pensado para tests. */
export const resetNotifier = (): void => {
  currentNotifier = consoleNotifier;
};

const emit =
  (level: NotificationLevel) =>
  (title: string, options?: NotificationOptions): void => {
    try {
      currentNotifier(level, title, options);
    } catch {
      // Un notificador roto no puede tumbar la operación que lo disparó.
      consoleNotifier(level, title, options);
    }
  };

export const notify = {
  info: emit('info'),
  success: emit('success'),
  warning: emit('warning'),
  error: emit('error'),
};
